import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Mail, Clock, User, Paperclip, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  sent:   { label: "Sent",   cls: "bg-green-100 text-green-800" },
  test:   { label: "Test",   cls: "bg-yellow-100 text-yellow-800" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800" },
};

const EMAIL_TYPE_LABEL = {
  ticket:                 "E-Ticket",
  invoice:                "Invoice",
  invoice_reminder:       "Invoice Reminder",
  receipt:                "Receipt",
  credit_note:            "Credit Note",
  cancellation_doc:       "Cancellation Doc",
  booking_refund:         "Booking Refund",
  service_confirmation:   "Service Confirmation",
  service_refund:         "Service Refund",
  visa_quote:             "Visa Quote",
  visa_payment:           "Visa Payment",
  visa_pay_later:         "Visa Pay Later",
  hotel_voucher:          "Hotel Voucher",
  hotel_quote:            "Hotel Quote",
  hotel_confirmation:     "Hotel Confirmation",
  hotel_amendment:        "Hotel Amendment",
  hotel_cancellation:     "Hotel Cancellation",
  umrah_quotation:        "Umrah Quotation",
  provisional_itinerary:  "Provisional Itinerary",
  confirmed_itinerary:    "Confirmed Itinerary",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function EntryRow({ entry, onViewBody }) {
  const [open, setOpen] = useState(false);
  const badge = STATUS_BADGE[entry.status] || { label: entry.status, cls: "bg-gray-100 text-gray-700" };
  const typeLabel = EMAIL_TYPE_LABEL[entry.email_type] || entry.email_type;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Mail size={15} className="text-gray-400 flex-shrink-0" />
          <span className="font-medium text-sm text-gray-800 truncate">{typeLabel}</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0 ml-2">
          <span className="text-xs text-gray-500 hidden sm:block">{formatDate(entry.sent_at)}</span>
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-200 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-gray-500 w-16 flex-shrink-0">Subject</span>
            <span className="text-gray-800 break-words">{entry.subject || "—"}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-gray-500 w-16 flex-shrink-0">To</span>
            <span className="text-gray-800 break-all">{(entry.to || []).join(", ") || "—"}</span>
          </div>
          {entry.cc && entry.cc.length > 0 && (
            <div className="flex items-start gap-2">
              <span className="text-gray-500 w-16 flex-shrink-0">CC</span>
              <span className="text-gray-800 break-all">{entry.cc.join(", ")}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Clock size={12} className="text-gray-400" />
            <span className="text-gray-600">{formatDate(entry.sent_at)}</span>
          </div>
          {entry.sent_by && (
            <div className="flex items-center gap-2">
              <User size={12} className="text-gray-400" />
              <span className="text-gray-600">{entry.sent_by}</span>
            </div>
          )}
          {entry.attachments_meta && entry.attachments_meta.length > 0 && (
            <div className="flex items-start gap-2">
              <Paperclip size={12} className="text-gray-400 mt-0.5" />
              <div className="flex flex-wrap gap-1">
                {entry.attachments_meta.map((a, i) => (
                  <span key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                    {a.filename}
                  </span>
                ))}
              </div>
            </div>
          )}
          <Button
            size="sm"
            variant="outline"
            className="mt-2 h-7 text-xs"
            onClick={() => onViewBody(entry.id)}
          >
            <Eye size={12} className="mr-1" />
            View email body
          </Button>
        </div>
      )}
    </div>
  );
}

function EmailBodyModal({ logId, onClose, getAuthHeaders }) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API}/communication-log/${logId}`, { headers: getAuthHeaders() });
        if (!cancelled) setHtml(res.data.html_body || "");
      } catch {
        if (!cancelled) setError("Could not load email body.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [logId, getAuthHeaders]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <span className="font-semibold text-sm text-gray-800">Email Body — Sent Copy</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && (
            <iframe
              title="Email preview"
              srcDoc={html}
              className="w-full border rounded"
              style={{ minHeight: "500px" }}
              sandbox=""
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * CommunicationHistory — reusable component.
 *
 * Props:
 *   sourceType  {string}  e.g. "ticket", "invoice", "booking"
 *   sourceId    {string}  the document's id
 *   bookingId   {string?} optional — if provided, shows all comms for the booking
 *   title       {string?} section heading (default: "Communication History")
 */
export default function CommunicationHistory({ sourceType, sourceId, bookingId, title = "Communication History" }) {
  const { getAuthHeaders, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [viewingId, setViewingId] = useState(null);

  const load = useCallback(async (signal) => {
    if (!token) return;
    if (!sourceId && !bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const params = bookingId
        ? { booking_id: bookingId }
        : { source_type: sourceType, source_id: sourceId };
      const res = await axios.get(`${API}/communication-log`, { params, headers: getAuthHeaders(), signal });
      setEntries(res.data.entries || []);
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (err.response?.status !== 400) {
        setError("Could not load communication history.");
      }
    } finally {
      setLoading(false);
    }
  }, [sourceType, sourceId, bookingId, token, getAuthHeaders]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <div className="mt-6">
      <button
        className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900"
        onClick={() => setCollapsed(c => !c)}
      >
        <Mail size={15} />
        {title}
        {entries.length > 0 && (
          <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full font-medium">
            {entries.length}
          </span>
        )}
        {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>

      {!collapsed && (
        <div>
          {loading && <p className="text-xs text-gray-500 py-2">Loading…</p>}
          {error && <p className="text-xs text-red-600 py-2">{error}</p>}
          {!loading && !error && entries.length === 0 && (
            <p className="text-xs text-gray-400 py-2 italic">No emails sent yet.</p>
          )}
          {entries.map(entry => (
            <EntryRow key={entry.id} entry={entry} onViewBody={setViewingId} />
          ))}
        </div>
      )}

      {viewingId && (
        <EmailBodyModal
          logId={viewingId}
          onClose={() => setViewingId(null)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}
